// ViewTimetable.jsx
import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";

const ViewTimetable = () => {
  const [selected, setSelected] = useState({ branch: "", semester: "", section: "" });
  const [branches, setBranches] = useState([]);
  const [timetable, setTimetable] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedTimetable, setEditedTimetable] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [facultyMap, setFacultyMap] = useState({});
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);

  const [sectionsList, setSectionsList] = useState(["A", "B", "C", "D"]);
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    const fetchSections = async () => {
      try {
        let params = {};
        if (selected.branch) params.branch = selected.branch;
        if (selected.semester) params.semester = selected.semester;
        const res = await axios.get(`${baseApiURL()}/section/getSectionsByBranchAndSemester`, { params });
        if (res.data.success && res.data.sections?.length > 0) {
          setSectionsList(res.data.sections);
        }
      } catch (err) {
        console.error("Error fetching dynamic sections:", err);
      }
    };
    fetchSections();
  }, [selected.branch, selected.semester]);

  useEffect(() => {
    fetchBranches();
    fetchSubjects();
    fetchFaculties();
  }, []);

  // Create faculty map for easy lookup
  useEffect(() => {
    const map = {};
    faculties.forEach(faculty => {
      map[faculty._id] = `${faculty.firstName} ${faculty.lastName}`;
      // Also map by name for search
      map[`${faculty.firstName} ${faculty.lastName}`] = faculty._id;
    });
    setFacultyMap(map);
  }, [faculties]);

  // Filter subjects when branch or semester changes
  useEffect(() => {
    if (selected.branch && selected.semester) {
      const filtered = subjects.filter(
        (subject) => 
          subject.semester === parseInt(selected.semester) && 
          subject.branch?.name === selected.branch
      );
      setFilteredSubjects(filtered);
    } else {
      setFilteredSubjects([]);
    }
  }, [selected.branch, selected.semester, subjects]);

  // Update available days when timetable changes
  useEffect(() => {
    if (timetable && timetable.schedule) {
      const currentDays = timetable.schedule.map(day => day.day);
      setAvailableDays(currentDays);
    }
  }, [timetable]);

  const fetchBranches = () => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((res) => {
        if (res.data.success) {
          setBranches(res.data.branches);
        }
      })
      .catch(() => {
        toast.error("Failed to fetch branches");
      });
  };

  const fetchSubjects = () => {
    axios.get(`${baseApiURL()}/subject/getSubject`)
      .then((res) => {
        if (res.data.success) {
          setSubjects(res.data.subject);
        }
      })
      .catch(() => {
        toast.error("Failed to fetch subjects");
      });
  };

  const fetchFaculties = () => {
    axios.get(`${baseApiURL()}/faculty/details/getDetails2`)
      .then((res) => {
        if (res.data.success) {
          setFaculties(res.data.faculties);
        }
      })
      .catch(() => {
        toast.error("Failed to fetch faculty data");
      });
  };

  const fetchTimetable = () => {
    if (!selected.branch || !selected.semester || !selected.section) {
      toast.error("Please select all fields");
      return;
    }

    toast.loading("Fetching timetable...");
    axios
      .post(`${baseApiURL()}/timetable/getTimetable`, {
        branch: selected.branch,
        semester: selected.semester,
        section: selected.section,
      })
      .then((res) => {
        toast.dismiss();
        if (res.data.success && res.data.timetable.length > 0) {
          const fetchedTimetable = res.data.timetable[0];
          setTimetable(fetchedTimetable);
          setEditedTimetable(JSON.parse(JSON.stringify(fetchedTimetable)));
          
          // Set available days
          const currentDays = fetchedTimetable.schedule.map(day => day.day);
          setAvailableDays(currentDays);
        } else {
          setTimetable(null);
          setEditedTimetable(null);
          setAvailableDays([]);
          toast.error("No timetable found");
        }
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Error fetching timetable");
      });
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditedTimetable(JSON.parse(JSON.stringify(timetable)));
    
    // Reset available days to original
    if (timetable) {
      const currentDays = timetable.schedule.map(day => day.day);
      setAvailableDays(currentDays);
    }
  };

  const handleSave = () => {
    toast.loading("Saving timetable...");
    axios
      .put(`${baseApiURL()}/timetable/editTimetable/${editedTimetable._id}`, {
        semester: editedTimetable.semester,
        branch: editedTimetable.branch,
        section: editedTimetable.section,
        schedule: JSON.stringify(editedTimetable.schedule),
        metadata: editedTimetable.metadata || {}
      })
      .then((res) => {
        toast.dismiss();
        if (res.data.success) {
          toast.success(res.data.message);
          setTimetable(res.data.timetable);
          setEditedTimetable(JSON.parse(JSON.stringify(res.data.timetable)));
          setEditMode(false);
          
          // Update available days
          const currentDays = res.data.timetable.schedule.map(day => day.day);
          setAvailableDays(currentDays);
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        toast.dismiss();
        console.error(err);
        toast.error(err.response?.data?.message || "Error updating timetable");
      });
  };

  const updatePeriod = (dayIndex, periodIndex, field, value) => {
    const updatedSchedule = [...editedTimetable.schedule];
    updatedSchedule[dayIndex].periods[periodIndex][field] = value;
    setEditedTimetable({
      ...editedTimetable,
      schedule: updatedSchedule
    });
  };

  const addPeriod = (dayIndex) => {
    const updatedSchedule = [...editedTimetable.schedule];
    updatedSchedule[dayIndex].periods.push({
      periodNumber: updatedSchedule[dayIndex].periods.length + 1,
      subject: "",
      faculty: "",
      startTime: "",
      endTime: ""
    });
    setEditedTimetable({
      ...editedTimetable,
      schedule: updatedSchedule
    });
  };

  const removePeriod = (dayIndex, periodIndex) => {
    const updatedSchedule = [...editedTimetable.schedule];
    updatedSchedule[dayIndex].periods.splice(periodIndex, 1);
    
    // Update period numbers
    updatedSchedule[dayIndex].periods = updatedSchedule[dayIndex].periods.map((period, idx) => ({
      ...period,
      periodNumber: idx + 1
    }));
    
    setEditedTimetable({
      ...editedTimetable,
      schedule: updatedSchedule
    });
  };

  const addDay = (day) => {
    const updatedSchedule = [...editedTimetable.schedule];
    updatedSchedule.push({
      day: day,
      periods: [{
        periodNumber: 1,
        subject: "",
        faculty: "",
        startTime: "",
        endTime: ""
      }]
    });
    
    setEditedTimetable({
      ...editedTimetable,
      schedule: updatedSchedule
    });
    
    // Update available days
    setAvailableDays([...availableDays, day]);
  };

  const removeDay = (dayIndex) => {
    const updatedSchedule = [...editedTimetable.schedule];
    const removedDay = updatedSchedule[dayIndex].day;
    updatedSchedule.splice(dayIndex, 1);
    
    setEditedTimetable({
      ...editedTimetable,
      schedule: updatedSchedule
    });
    
    // Update available days
    setAvailableDays(availableDays.filter(day => day !== removedDay));
  };

  const maxPeriods = () => {
    const currentTimetable = editMode ? editedTimetable : timetable;
    if (!currentTimetable) return 0;
    return Math.max(...currentTimetable.schedule.map(day => day.periods.length));
  };

  const getFacultyName = (facultyId) => {
    if (!facultyId) return '';
    
    // Check if facultyId is already a name (not an ObjectId)
    if (typeof facultyId === 'string' && facultyId.length > 24) {
      return facultyId; // It's already a name
    }
    
    // Look up in facultyMap
    if (facultyMap[facultyId]) {
      return facultyMap[facultyId];
    }
    
    // Try to find in faculties array
    const faculty = faculties.find(f => f._id === facultyId);
    if (faculty) {
      return `${faculty.firstName} ${faculty.lastName}`;
    }
    
    return facultyId; // Return as is if not found
  };

  // New function to get class incharge name
  const getClassInchargeName = (classIncharge) => {
    if (!classIncharge) return '';
    
    // If it's an ID (24 character hex string)
    if (typeof classIncharge === 'string' && classIncharge.length === 24 && /^[0-9a-fA-F]{24}$/.test(classIncharge)) {
      // Look up in facultyMap
      if (facultyMap[classIncharge]) {
        return facultyMap[classIncharge];
      }
      
      // Try to find in faculties array
      const faculty = faculties.find(f => f._id === classIncharge);
      if (faculty) {
        return `${faculty.firstName} ${faculty.lastName}`;
      }
    }
    
    // If it's already a name or something else
    return classIncharge;
  };

  const formatTimeRange = (period) => {
    if (period.startTime && period.endTime) {
      return `${period.startTime} - ${period.endTime}`;
    }
    return '';
  };

  return (
    <div className="w-full space-y-6">
      <div className="bento-card p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <select
            className="w-full p-2.5 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            value={selected.branch}
            onChange={(e) => setSelected({ ...selected, branch: e.target.value })}
            disabled={editMode}
          >
            <option value="">Select Branch</option>
            {branches.map((b) => (
              <option key={b.name} value={b.name}>{b.name}</option>
            ))}
          </select>

          <select
            className="w-full p-2.5 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            value={selected.semester}
            onChange={(e) => setSelected({ ...selected, semester: e.target.value })}
            disabled={editMode}
          >
            <option value="">Select Semester</option>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
              <option key={sem} value={sem}>{sem} Semester</option>
            ))}
          </select>

          <select
            className="w-full p-2.5 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            value={selected.section}
            onChange={(e) => setSelected({ ...selected, section: e.target.value })}
            disabled={editMode}
          >
            <option value="">Select Section</option>
            {sectionsList.map((sec) => (
              <option key={sec} value={sec}>Section {sec}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-4 items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="flex gap-2">
            <button
              onClick={fetchTimetable}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-xs transition cursor-pointer"
              disabled={editMode}
            >
              View Timetable
            </button>
            
            {timetable && !editMode && (
              <button
                onClick={handleEdit}
                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-xs transition cursor-pointer"
              >
                Edit Timetable
              </button>
            )}
          </div>
          
          {selected.branch && selected.semester && (
            <div className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-3 py-1.5 rounded-xl border border-indigo-100 dark:border-indigo-800/40">
              {filteredSubjects.length} subject(s) available for {selected.branch} - Semester {selected.semester}
            </div>
          )}
        </div>
      </div>

      {editMode && (
        <div className="space-y-4">
          <div className="bg-indigo-50 dark:bg-slate-950 p-4 rounded-2xl border border-indigo-100 dark:border-slate-800">
            <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-900 dark:text-indigo-300 mb-2">Add Days:</h3>
            <div className="flex flex-wrap gap-2">
              {daysOfWeek.map(day => (
                !availableDays.includes(day) && (
                  <button
                    key={day}
                    onClick={() => addDay(day)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs cursor-pointer shadow-xs"
                  >
                    + Add {day}
                  </button>
                )
              ))}
            </div>
          </div>
        </div>
      )}

      {timetable && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
          <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">Timetable Schedule</h2>
          </div>
          
          {timetable.metadata && (
            <div className="bg-slate-50 dark:bg-slate-950 p-4 border-b border-slate-100 dark:border-slate-800 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              {timetable.metadata.lectureHall && (
                <div><span className="font-bold text-slate-500 dark:text-slate-400">Lecture Hall:</span> <strong className="text-slate-900 dark:text-white">{timetable.metadata.lectureHall}</strong></div>
              )}
              {timetable.metadata.effectiveDate && (
                <div><span className="font-bold text-slate-500 dark:text-slate-400">Effective From:</span> <strong className="text-slate-900 dark:text-white">{timetable.metadata.effectiveDate}</strong></div>
              )}
              {timetable.metadata.classIncharge && (
                <div><span className="font-bold text-slate-500 dark:text-slate-400">Class Incharge:</span> <strong className="text-slate-900 dark:text-white">{getClassInchargeName(timetable.metadata.classIncharge)}</strong></div>
              )}
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
              <thead className="bg-slate-50 dark:bg-slate-950">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Day</th>
                  {Array.from({ length: maxPeriods() }).map((_, i) => (
                    <th key={i} className="px-4 py-3 text-center text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Period {i + 1}</th>
                  ))}
                  {editMode && <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {(editMode ? editedTimetable.schedule : timetable.schedule).map((dayEntry, dayIndex) => (
                  <tr key={dayEntry.day} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3 font-bold text-center text-slate-900 dark:text-white">
                      {dayEntry.day}
                      {editMode && (
                        <button
                          className="ml-2 px-2 py-0.5 bg-rose-600 text-white rounded-lg text-[10px] font-bold"
                          onClick={() => removeDay(dayIndex)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                    {Array.from({ length: maxPeriods() }).map((_, periodIndex) => {
                      const period = dayEntry.periods[periodIndex];
                      return (
                        <td key={periodIndex} className="px-3 py-2 text-xs text-center border-l border-slate-100 dark:border-slate-800">
                          {period ? (
                            editMode ? (
                              <div className="flex flex-col gap-1.5 p-1">
                                <select
                                  className="p-1 border border-slate-200 dark:border-slate-700 rounded-lg text-xs bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                                  value={period.subject}
                                  onChange={(e) => updatePeriod(dayIndex, periodIndex, 'subject', e.target.value)}
                                >
                                  <option value="">Select Subject</option>
                                  <option value="Break">Break</option>
                                  <option value="Sports">Sports</option>
                                  <option value="Library">Library</option>
                                  {filteredSubjects.map((subj) => (
                                    <option key={subj._id} value={subj.name}>
                                      {subj.name} ({subj.code})
                                    </option>
                                  ))}
                                </select>

                                <select
                                  className="p-1 border border-slate-200 dark:border-slate-700 rounded-lg text-xs bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                                  value={period.faculty}
                                  onChange={(e) => updatePeriod(dayIndex, periodIndex, 'faculty', e.target.value)}
                                  disabled={["Break", "Sports", "Library"].includes(period.subject)}
                                >
                                  <option value="">Select Faculty</option>
                                  {faculties.map((f) => (
                                    <option key={f._id} value={f._id}>
                                      {f.firstName} {f.lastName}
                                    </option>
                                  ))}
                                </select>

                                <button
                                  className="px-2 py-1 bg-rose-600 text-white rounded-lg text-[10px] font-bold"
                                  onClick={() => removePeriod(dayIndex, periodIndex)}
                                >
                                  Remove
                                </button>
                              </div>
                            ) : (
                              <div className="p-1">
                                <div className="font-bold text-slate-900 dark:text-white">{period.subject}</div>
                                {!["Break", "Sports", "Library"].includes(period.subject) && (
                                  <div className="text-slate-600 dark:text-slate-400 text-[11px] mt-0.5">
                                    {getFacultyName(period.faculty)}
                                  </div>
                                )}
                                <div className="text-slate-400 text-[10px] mt-0.5 font-mono">
                                  {formatTimeRange(period)}
                                </div>
                              </div>
                            )
                          ) : (
                            "-"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {editMode && (
            <div className="flex justify-center gap-3 p-4 bg-slate-50 dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={handleSave}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewTimetable;